import { useDashboardReview } from './hooks/useDashboardReview'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import { TaskOverviewContentType, useTaskOverviewContext } from './TaskOverviewStackProvider'

export interface IMediaSubtaskPager {
  /** Whether the header pager should be rendered at all. */
  showSubtaskNav: boolean
  /** 1-based position of the current media subtask. */
  position: number
  /** Total number of media subtasks. */
  total: number
  /** Result status of the current media subtask (null = no result yet). */
  status: number | null
  hasPrev: boolean
  hasNext: boolean
  goPrev: () => void
  goNext: () => void
  /** True while media is uploading — the pager controls should be disabled. */
  isBusy: boolean
}

/**
 * Pager for navigating between the media (image/video) subtasks of a VM task
 * directly from the media-completing screen, without popping back to MAIN.
 * Used by both auditor and executor. All state comes from TaskOverviewStackProvider.
 */
export const useMediaSubtaskPager = (): IMediaSubtaskPager => {
  const review = useDashboardReview()
  const {
    task,
    contentStack,
    mediaSubtasksOrdered,
    navigateToSubtask,
    currentSubtaskStatus,
    isSubtaskBusy,
    isReadMode,
    isSubtaskReviewable,
  } = useTaskOverviewContext()

  const source = useSelector((state: RootState) => state.tasks_v2.subtasks[task.tid])
  const allSubtasks = useMemo(() => [...(source || [])].sort((a, b) => a.order_index - b.order_index), [source])
  const ordered =
    review?.subtasks ||
    (contentStack.some((frame) => frame.contentType === TaskOverviewContentType.MAIN)
      ? allSubtasks
      : mediaSubtasksOrdered)
  const currentContent = contentStack[contentStack.length - 1]
  const selectedSubtask = currentContent?.props?.selectedSubtask
  const isMediaCompleting = currentContent?.contentType === TaskOverviewContentType.MEDIA_SUBTASK_COMPLETING

  const total = ordered.length
  const index = selectedSubtask ? ordered.findIndex((s) => s.st_id === selectedSubtask.st_id) : -1

  const showSubtaskNav = isMediaCompleting && (total > 1 || !!review) && index >= 0

  // Auditors step only onto subtasks with an actual submitted result — not-yet-done or
  // skipped subtasks have nothing to review, so the pager jumps straight past them instead of
  // landing there. The executor still needs every subtask reachable to complete pending ones.
  const findReviewableIndex = (fromIndex: number, direction: 1 | -1): number => {
    let i = fromIndex + direction
    while (i >= 0 && i < total) {
      if (review || !isReadMode || isSubtaskReviewable(ordered[i])) return i
      i += direction
    }
    return -1
  }

  const nextIndex = index >= 0 ? findReviewableIndex(index, 1) : -1
  const prevIndex = index >= 0 ? findReviewableIndex(index, -1) : -1

  const hasPrev = prevIndex !== -1
  const hasNext = nextIndex !== -1

  const goPrev = () => {
    if (!isSubtaskBusy && hasPrev) navigateToSubtask(ordered[prevIndex])
  }
  const goNext = () => {
    if (!isSubtaskBusy && hasNext) navigateToSubtask(ordered[nextIndex])
  }

  return {
    showSubtaskNav,
    position: index + 1,
    total,
    status: currentSubtaskStatus,
    hasPrev,
    hasNext,
    goPrev,
    goNext,
    isBusy: isSubtaskBusy,
  }
}
